import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useTheme } from '@/context/ThemeContext'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import UserMenu from '@/components/common/UserMenu'
import {
  Building2,
  Users,
  CreditCard,
  BarChart3,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Menu,
  X,
  Key,
  Settings,
  Activity,
  Image,
  Database,
  Globe,
  Laptop,
  Receipt,
  ToggleRight
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'

interface AdminLayoutProps {
  children: React.ReactNode
}

const navigation = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: BarChart3 },
  { name: 'Restaurants', href: '/admin/restaurants', icon: Building2 },
  { name: 'Feature List', href: '/admin/feature-list', icon: ToggleRight },
  { name: 'Subscriptions', href: '/admin/subscriptions', icon: CreditCard },
  { name: 'Users', href: '/admin/users', icon: Users },
  { name: 'API Keys', href: '/admin/api-keys', icon: Key },
  { name: 'Devices', href: '/admin/devices', icon: Laptop },
  { name: 'Ordore Mini', href: '/admin/ordore-mini', icon: Receipt },
  { name: 'DB Management', href: '/admin/db-management', icon: Database },
  { name: 'Portal Settings', href: '/admin/portal-settings', icon: Settings },
  { name: 'Validation Logs', href: '/admin/validation-logs', icon: Activity },
  { name: 'Banner', href: '/admin/banner', icon: Image },
  { name: 'Online Integrations', href: '/admin/online-integrations', icon: Globe },
]

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logout } = useAuth()
  const { theme, setTheme } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const toggleTheme = () => {
    if (theme === 'light') setTheme('dark')
    else if (theme === 'dark') setTheme('system')
    else setTheme('light')
  }

  const getThemeIcon = () => {
    if (theme === 'light') return <Sun className="h-4 w-4" />
    if (theme === 'dark') return <Moon className="h-4 w-4" />
    return <Monitor className="h-4 w-4" />
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 transform bg-card border-r transition-transform duration-200 ease-in-out md:translate-x-0",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex h-16 items-center justify-between px-6 border-b">
            <div className="flex items-center space-x-2">
              <Building2 className="h-6 w-6 text-restaurant-primary" />
              <span className="font-semibold">Admin Panel</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsSidebarOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto thin-scrollbar space-y-2 p-4">
            {navigation.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.href
              
              return (
                <Button
                  key={item.name}
                  variant={isActive ? "secondary" : "ghost"}
                  className={cn(
                    "w-full justify-start space-x-2",
                    isActive && "bg-restaurant-primary/10 text-restaurant-primary hover:bg-restaurant-primary/20"
                  )}
                  onClick={() => {
                    navigate(item.href)
                    setIsSidebarOpen(false)
                  }}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.name}</span>
                </Button>
              )
            })}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t space-y-2">
            <div className="flex items-center space-x-2 px-2 py-1">
              <div className="w-8 h-8 rounded-full bg-restaurant-primary/10 flex items-center justify-center">
                <Users className="h-4 w-4 text-restaurant-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground">Administrator</p>
              </div>
            </div>
            <div className="flex space-x-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="flex-1"
              >
                {getThemeIcon()}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="flex-1"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-30 flex h-16 items-center justify-between border-b bg-card/80 backdrop-blur-sm px-4 md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsSidebarOpen(true)}
          >
            <Menu className="h-4 w-4" />
          </Button>
          
          <div className="flex items-center space-x-4">
            <h1 className="text-lg font-semibold capitalize">
              {location.pathname.split('/').pop()?.replace('-', ' ')}
            </h1>
          </div>

          <div className="flex items-center space-x-2">
            <UserMenu />
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  )
}